import React from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import ErrorElement from "./ErrorElement/ErrorElement";
import PATHS from "./paths";
import ProtectedRoute from "./ProtectedRoute/ProtectedRoute";
import RedirectRoute from "./RedirectRoute/RedirectRoute";

import SignIn from "@/features/auth/views/SignIn";
import SignUp from "@/features/auth/views/SignUp";
import AuthLayout from "@/layouts/AuthLayout/AuthLayout";
import { AxiosInterceptorProvider } from "@/libs/axios";
import DashboardLayoutWrapper from "@/wrappers/DashboardLayoutWrapper/DashboardLayoutWrapper";
import WorkspaceWrapper from "@/features/workspaces/wrappers/WorkspaceWrapper.tsx";
import CalendarPage from "@/features/calendar/views/CalendarPage.tsx";
import GetUserDataWrapper from "@/wrappers/GetUserDataWrapper/GetUserDataWrapper.tsx";
import TutorProfilePage from "@/features/profile/tutor/views/ProfilePage.tsx";
import GetProfileDataWrapper from "@/wrappers/GetProfileDataWrapper/GetProfileDataWrapper.tsx";
import CreateProfilePage from "@/features/profile/tutor/views/CreateProfilePage.tsx";
import JoinWorkspace from "@/features/workspaces/student/views/JoinWorkspace.tsx";
import LogOut from "@/features/auth/views/LogOut.tsx";
import { StudentsWorkspaceSchedule } from "@/features/workspaces/student/views/StudentsWorkspaceSchedule.tsx";
import { StudentsWorkspace } from "@/features/workspaces/student/views/StudentsWorkspace.tsx";
import GetPublicProfileDataWrapper from "@/wrappers/GetProfileDataWrapper/GetPublicProfileDataWrapper";
import PublicProfilePage from "@/features/profile/public/views/PublicProfilePage";
import PublicReviewsPage from "@/features/profile/public/views/PublicReviewsPage.tsx";
import { WorkspaceChat } from "@/features/chat/views/WorkspaceChat.tsx";
import { TutorsWorkspace } from "@/features/workspaces/tutor/views/TutorsWorkspace.tsx";
import ClassroomWrapper from "@/features/classroom/wrappers/ClassroomWrapper.tsx";
import Whiteboard from "@/features/classroom/components/Whiteboard";
import { LessonDetailsPage as TutorLessonDetailsPage } from "@/features/workspaces/tutor/views/LessonDetailsPage.tsx";
import { LessonDetailsPage as StudentLessonDetailsPage } from "@/features/workspaces/student/views/LessonDetailsPage.tsx";
import TemplatesList from "@/features/calendar/views/TemplatesList.tsx";
import TemplateEditView from "@/features/calendar/views/TemplateEditView.tsx";
import TooManyRequests from "@/routes/ErrorElement/TooManyRequests.tsx";
import { ScheduleSettingsPage } from "@/features/calendar/views/ScheduleSettingsPage.tsx";
import { LessonPricingPage } from "@/features/calendar/components/settings/pricing/LessonPricingPage.tsx";
import PipModeProvider from "@/features/classroom/providers/PipModeProvider.tsx";
import { TutorMaterials } from "@/features/workspaces/tutor/components/TutorMaterials.tsx";
import { TutorLessonListings } from "@/features/workspaces/tutor/components/TutorLessonListings.tsx";
import { StudentLessonListings } from "@/features/workspaces/student/components/StudentLessonListings.tsx";
import { StudentMaterials } from "@/features/workspaces/student/components/StudentMaterials.tsx";
import { PayoutsPage } from "@/features/subscriptions/tutor/views/PayoutsPage.tsx";
import { StudentWorkspaceDashboard } from "@/features/workspaces/student/components/StudentWorkspaceDashboard.tsx";
import TutorWorkspaceDashboard from "@/features/workspaces/tutor/views/TutorWorkspaceDashboard.tsx";
// Import new pages
import FinancesOverviewPage from "@/features/finances/views/FinancesOverviewPage.tsx";
import PaymentsHistoryPage from "@/features/finances/views/PaymentsHistoryPage.tsx";
import PaymentDetailsPage from "@/features/finances/views/PaymentDetailsPage";
import TechnicalSupportPage from "@/features/support/views/TechnicalSupportPage.tsx";
import TutorsPage from "@/features/workspaces/student/views/TutorsPage.tsx";
import AnnouncementsPage from "@/features/workspaces/student/views/AnnouncementsPage.tsx";
import WorkspaceSettingsPage from "@/features/workspaces/shared/views/WorkspaceSettingsPage.tsx";
import StudentsPage from "@/features/workspaces/tutor/views/StudentsPage.tsx";
import ChatsPage from "@/features/workspaces/shared/views/ChatsPage.tsx";
import WorkspacesListPage from "@/features/workspaces/views/WorkspacesListPage.tsx";
import InvitationsPage from "@/features/workspaces/tutor/views/InvitationsPage.tsx";
import CheckoutStatusPage from "@/features/finances/views/CheckoutStatusPage.tsx";
import FinancesWrapper from "@/features/finances/wrappers/FinancesWrapper.tsx";
import { OnboardingLayout } from "@/layouts/OnboardingLayout/OnboardingLayout.tsx";
import OnboardingPage from "@/features/onboarding/views/OnboardingPage.tsx";
// Student finances pages
import StudentPaymentsHistoryPage from "@/features/finances/views/StudentPaymentsHistoryPage.tsx";
import StudentPaymentDetailsPage from "@/features/finances/views/StudentPaymentDetailsPage.tsx";
import TermsOfService from "@/features/auth/views/TermsOfService.tsx";
import PrivacyPolicy from "@/features/auth/views/PrivacyPolicy.tsx";

const BuyCreditsPage = React.lazy(() => import("@/features/finances/views/BuyCreditsPage.tsx"));
const router = createBrowserRouter([
  {
    path: PATHS.BASE,
    element: (
      <AxiosInterceptorProvider>
        <GetUserDataWrapper>
          <PipModeProvider>
            <Outlet />
          </PipModeProvider>
        </GetUserDataWrapper>
      </AxiosInterceptorProvider>
    ),
    errorElement: <ErrorElement />,
    children: [
      {
        path: PATHS.TOO_MANY_REQUESTS,
        element: <TooManyRequests />,
      },
      { path: PATHS.REDIRECT, element: <RedirectRoute /> },
      {
        path: PATHS.TERMS_OF_SERVICE,
        element: (
          <ProtectedRoute accessFor={"all"}>
            <TermsOfService />
          </ProtectedRoute>
        ),
      },
      {
        path: PATHS.PRIVACY_POLICY,
        element: (
          <ProtectedRoute accessFor={"all"}>
            <PrivacyPolicy />
          </ProtectedRoute>
        ),
      },
      {
        path: PATHS.BASE,
        element: <AuthLayout />,
        children: [
          { index: true, element: <Navigate to={PATHS.SIGN_IN} /> },
          {
            path: PATHS.SIGN_IN,
            element: (
              <ProtectedRoute accessFor={["notAuthorized"]}>
                <SignIn />
              </ProtectedRoute>
            ),
          },
          {
            path: PATHS.SIGN_UP,
            element: (
              <ProtectedRoute accessFor={["notOnboarded", "notAuthorized"]}>
                <SignUp />
              </ProtectedRoute>
            ),
          },
          {
            path: PATHS.LOG_OUT,
            element: (
              <ProtectedRoute accessFor={"all"}>
                <LogOut />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: PATHS.PROFILES.TUTOR_PROFILE,
        element: (
          <ProtectedRoute accessFor={"all"}>
            <DashboardLayoutWrapper>
              <GetPublicProfileDataWrapper>
                <Outlet />
              </GetPublicProfileDataWrapper>
            </DashboardLayoutWrapper>
          </ProtectedRoute>
        ),
        children: [
          {
            path: `:profileId/:locale?`,
            element: <PublicProfilePage />,
          },
          {
            path: `reviews/:profileId/:locale?`,
            element: <PublicReviewsPage />,
          },
        ],
      },
      {
        path: PATHS.TUTOR.ONBOARDING.INDEX,
        element: (
          <ProtectedRoute
            accessFor={[
              "tutorWithoutProfile",
              "tutorWithoutPricing",
              "tutorWithoutPayments",
              "tutorWithoutWorkspace",
            ]}
          >
            <OnboardingLayout>
              <Outlet />
            </OnboardingLayout>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={PATHS.TUTOR.ONBOARDING.PROFILE} />,
          },
          {
            path: "profile",
            element: <OnboardingPage />,
          },
          {
            path: "pricing",
            element: <OnboardingPage />,
          },
          {
            path: "payments",
            element: <OnboardingPage />,
          },
          {
            path: "invite",
            element: <OnboardingPage />,
          },
        ],
      },
      {
        path: PATHS.TUTOR.DASHBOARD,
        element: (
          <ProtectedRoute accessFor={["tutor", "tutorInWorkspace"]}>
            <DashboardLayoutWrapper>
              <Outlet />
            </DashboardLayoutWrapper>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={PATHS.TUTOR.OVERVIEW} />,
          },
          {
            path: PATHS.TUTOR.OVERVIEW,
            element: <TutorWorkspaceDashboard />,
          },
          {
            path: `${PATHS.TUTOR.PUBLIC_PROFILE}/:locale?`,
            element: (
              <GetProfileDataWrapper>
                <TutorProfilePage />
              </GetProfileDataWrapper>
            ),
          },
          {
            path: PATHS.TUTOR.SCHEDULE.PRICING,
            element: <LessonPricingPage />,
          },
          {
            path: `${PATHS.TUTOR.CREATE_PROFILE_NL}`,
            element: (
              <GetProfileDataWrapper>
                <CreateProfilePage />
              </GetProfileDataWrapper>
            ),
          },
          {
            path: PATHS.TUTOR.WORKSPACES.WORKSPACES,
            element: (
              <WorkspaceWrapper>
                <Outlet />
              </WorkspaceWrapper>
            ),
            children: [
              {
                index: true,
                element: <Navigate to={`${PATHS.TUTOR.WORKSPACES.WORKSPACES}/main`} />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?`,
                element: <TutorsWorkspace />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/chat/:conversationId`,
                element: <WorkspaceChat />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/chats`,
                element: <ChatsPage />,
              },

              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId/lesson/:lessonId`,
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <Navigate replace to="video" />,
                  },
                  {
                    path: "video",
                    element: <ClassroomWrapper />,
                  },
                  {
                    path: "whiteboard",
                    element: <Whiteboard fullscreen />,
                  },
                ],
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/materials`,
                element: <TutorMaterials />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/lessons/listing`,
                element: <TutorLessonListings />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId/lessons/:lessonId/details`,
                element: <TutorLessonDetailsPage />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/students`,
                element: <StudentsPage />,
              },
              {
                path: `${PATHS.TUTOR.WORKSPACES.WORKSPACES}/:workspaceId?/settings`,
                element: <WorkspaceSettingsPage />,
              },
            ],
          },
          {
            path: PATHS.TUTOR.CHAT,
            element: <WorkspaceChat />,
          },
          {
            path: `${PATHS.TUTOR.SCHEDULE.INDEX}`,
            element: <Outlet />,
            children: [
              {
                path: PATHS.TUTOR.CALENDAR,
                element: <CalendarPage />,
              },
              {
                path: PATHS.TUTOR.SCHEDULE.TEMPLATES,
                element: <TemplatesList />,
              },
              {
                path: PATHS.TUTOR.SCHEDULE.SETTINGS,
                element: <ScheduleSettingsPage />,
              },
              {
                path: `${PATHS.TUTOR.SCHEDULE.TEMPLATES}/:templateId/edit`,
                element: <TemplateEditView />,
              },
            ],
          },
          {
            path: PATHS.TUTOR.FINANCES.INDEX,
            element: (
              <FinancesWrapper>
                <Outlet />
              </FinancesWrapper>
            ),
            children: [
              {
                path: PATHS.TUTOR.FINANCES.OVERVIEW,
                element: <FinancesOverviewPage />,
              },
              {
                path: PATHS.TUTOR.FINANCES.BUY_CREDITS,
                element: <BuyCreditsPage />,
              },
              {
                path: `${PATHS.TUTOR.FINANCES.CHECKOUT_STATUS}/:sessionId/:paymentId`,
                element: <CheckoutStatusPage />,
              },
            ],
          },
          {
            path: PATHS.TUTOR.FINANCES.PAYMENTS_HISTORY,
            element: <PaymentsHistoryPage />,
          },
          {
            path: PATHS.TUTOR.FINANCES.PAYMENT_DETAILS,
            element: <PaymentDetailsPage />,
          },
          {
            path: PATHS.TUTOR.FINANCES.PAYOUTS,
            element: <PayoutsPage />,
          },
          {
            path: PATHS.TUTOR.SUPPORT.TECHNICAL,
            element: <TechnicalSupportPage />,
          },
          {
            path: PATHS.TUTOR.WORKSPACES.LIST,
            element: <WorkspacesListPage />,
          },
          {
            path: PATHS.TUTOR.WORKSPACES.INVITATIONS,
            element: <InvitationsPage />,
          },
        ],
      },
      {
        path: PATHS.STUDENT.DASHBOARD,
        element: (
          <ProtectedRoute accessFor={["student", "studentInWorkspace"]}>
            <DashboardLayoutWrapper>
              <Outlet />
            </DashboardLayoutWrapper>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={PATHS.STUDENT.OVERVIEW} />,
          },
          {
            path: PATHS.STUDENT.OVERVIEW,
            element: <StudentWorkspaceDashboard />,
          },
          {
            path: PATHS.STUDENT.CHAT,
            element: <WorkspaceChat />,
          },
          {
            path: PATHS.STUDENT.SUPPORT.TECHNICAL,
            element: <TechnicalSupportPage />,
          },
          {
            path: PATHS.STUDENT.WORKSPACES.LIST,
            element: <WorkspacesListPage />,
          },
          {
            path: PATHS.STUDENT.FINANCES.PAYMENTS,
            element: <StudentPaymentsHistoryPage />,
          },
          {
            path: PATHS.STUDENT.FINANCES.PAYMENT_DETAILS,
            element: <StudentPaymentDetailsPage />,
          },
          {
            path: PATHS.STUDENT.WORKSPACES.WORKSPACES,
            element: (
              <WorkspaceWrapper>
                <Outlet />
              </WorkspaceWrapper>
            ),
            children: [
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId?`,
                element: <StudentsWorkspace />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId?/chat/:conversationId`,
                element: <WorkspaceChat />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId?/chats`,
                element: <ChatsPage />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/lessons/listing`,
                element: <StudentLessonListings />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/lessons/:lessonId/details`,
                element: <StudentLessonDetailsPage />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/schedule`,
                element: <StudentsWorkspaceSchedule />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/materials`,
                element: <StudentMaterials />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.JOIN}/:workspaceId`,
                element: <JoinWorkspace />,
              },

              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/lesson/:lessonId`,
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <Navigate replace to="video" />,
                  },
                  {
                    path: "video",
                    element: <ClassroomWrapper />,
                  },
                  {
                    path: "whiteboard",
                    element: <Whiteboard fullscreen />,
                  },
                ],
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/tutors`,
                element: <TutorsPage />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/announcements`,
                element: <AnnouncementsPage />,
              },
              {
                path: `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/:workspaceId/settings`,
                element: <WorkspaceSettingsPage />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export default router;
